import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { auth } from './config/firebasedeconfig';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  getDocs,
  query,
  where,
  collectionGroup,
  onSnapshot
} from "firebase/firestore";
import { db } from './config/firebasedeconfig';
import pushSubscriptionService from './services/pushSubscriptionService';
import notificationService from './services/notificationService';
import {
  getLevelNumber,
  getLevelTitle,
  getUserActivityScore,
} from './components/levelUtils';

export const UserContext = createContext();

export const UserContextProvider = (props) => {
  const [username, setUsername] = useState(null);
  const [email, setEmail] = useState(null);
  const [banner, setBanner] = useState(null);
  const [avatar, setAvatar] = useState(null);
  const [userIsLoggedIn, setUserIsLoggedIn] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null); // Pour stocker l'intention de redirection
  const [messageCount, setMessageCount] = useState(0);

  // Utilisé pour le système d'appel vidéo
  const [userData, setUserData] = useState(null);

  const defaultSubscription = {
    planId: "free",
    billingPeriod: "monthly",
    paypalSubscriptionId: null,
    status: "none",
    currentPeriodEnd: null
  };

  const fetchUserData = async (user) => {
    if (!user || !user.uid) {
      console.warn("Utilisateur non connecté ou UID invalide.");
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        const { username: userUsername, email: userEmail, banner: userBanner, avatar: userAvatar } = data;
        const subscription = data.subscription || defaultSubscription;

        setUsername(userUsername);
        setEmail(userEmail);
        setBanner(userBanner);
        setAvatar(userAvatar);
        setUserIsLoggedIn(true);
        setIsLoggedIn(true);

        // Mettre à jour les données utilisateur complètes pour le système d'appel
        setUserData({
          ...data,

          uid: user.uid,
          username: userUsername,
          email: userEmail,
          avatar: userAvatar,
          avatarUrl: userAvatar,
          banner: userBanner,
          isOnline: true,
          subscription,
        });

        console.log("UserContext: données utilisateur récupérées avec succès");
      } else {
        console.warn("UserContext: document utilisateur non trouvé");
      }
    } catch (error) {
      console.error("UserContext: erreur lors de la récupération des données utilisateur", error);
    }
  };


  const updateContext = (newUsername, newEmail, newAvatar, newBanner, newIsLoggedIn, newUserIsLoggedIn) => {
    if (newUsername !== username) {
      setUsername(newUsername);
    }
    if (newEmail !== email) {
      setEmail(newEmail);
    }
    if (newAvatar !== avatar) {
      setAvatar(newAvatar);
    }
    if (newBanner !== banner) {
      setBanner(newBanner);
    }
    if (newIsLoggedIn !== isLoggedIn) {
      setIsLoggedIn(newIsLoggedIn);
    }
    if (newUserIsLoggedIn !== userIsLoggedIn) {
      setUserIsLoggedIn(newUserIsLoggedIn);
    }

    // Mettre à jour les données utilisateur complètes
    if (userId) {
      setUserData((currentUserData) => ({
        ...currentUserData,

        uid: userId,
        username: newUsername,
        email: newEmail,
        avatar: newAvatar,
        avatarUrl: newAvatar,
        banner: newBanner,
        isOnline: newIsLoggedIn,

        subscription:
          currentUserData?.subscription ||
          defaultSubscription,
      }));
    }
  };

  useEffect(() => {
    console.log("UserContext: mise en place de l'observateur onAuthStateChanged");
    let previousUserId = null; // Garder une trace de l'userId précédent

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        console.log("UserContext: utilisateur connecté", user.uid);
        previousUserId = user.uid; // Sauvegarder l'userId

        // Met à jour isOnline à true dans Firestore
        try {
          const userRef = doc(db, "users", user.uid);
          // Vérifier d'abord si le document existe
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            await updateDoc(userRef, { isOnline: true });
            console.log("UserContext: isOnline mis à jour à true");
          } else {
            console.log("UserContext: document utilisateur n'existe pas encore, skip update isOnline");
          }
        } catch (error) {
          console.error("UserContext: erreur lors de la mise à jour isOnline", error);
        }

        // Récupère les informations utilisateur
        setUserId(user.uid);
        fetchUserData(user);

        // Push: sync existing subscription without prompting (permission must already be granted)
        pushSubscriptionService
          .syncExistingSubscription(user.uid)
          .catch((e) => console.warn('[Push] syncExistingSubscription failed:', e));
      } else {
        console.log("UserContext: aucun utilisateur connecté");
        // Met à jour isOnline à false dans Firestore
        if (previousUserId) {
          try {
            const userRef = doc(db, "users", previousUserId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              await updateDoc(userRef, { isOnline: false });
              console.log("UserContext: isOnline mis à jour à false");
            }
          } catch (error) {
            console.error("UserContext: erreur lors de la mise à jour isOnline (déconnexion)", error);
          }
          previousUserId = null; // Réinitialiser après déconnexion
        }

        // Réinitialise le contexte utilisateur
        resetContext();
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []); // Retirer userId des dépendances

  const resetContext = () => {
    setIsLoggedIn(false);
    setUserIsLoggedIn(false);
    setUsername(null);
    setEmail(null);
    setAvatar(null);
    setBanner(null);
    setUserData(null);
  };

  useEffect(() => {
    console.log("UserContext: userData mis à jour", userData);
  }, [userData]);

  useEffect(() => {
    const handleBeforeUnload = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, { isOnline: false });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);


  // Compter les sujets et les réponses de l'utilisateur dans le forum.
  const fetchUserMessageCount = useCallback(async (authorId) => {
    let postCount;
    let repliesCount;

    try {
      const postsQuery = query(
        collection(db, "posts"),
        where("authorId", "==", authorId)
      );
      const postsSnapshot = await getDocs(postsQuery);
      postCount = postsSnapshot.size;
    } catch (error) {
      console.error("UserContext: échec du comptage des sujets", error);
    }

    try {
      const repliesQuery = query(
        collectionGroup(db, "replies"),
        where("authorId", "==", authorId)
      );
      const repliesSnapshot = await getDocs(repliesQuery);
      repliesCount = repliesSnapshot.size;
    } catch (error) {
      console.error("UserContext: échec du comptage des réponses", error);
    }

    // Ne pas remplacer un comptage incomplet par une valeur incorrecte.
    if (postCount === undefined || repliesCount === undefined) {
      return null;
    }

    return postCount + repliesCount;
  }, []);


  const checkAndNotifyLevelUp = useCallback(async (targetUserId, currentUserData = {}) => {
    if (!targetUserId) return;

    try {
      const currentMessageCount = await fetchUserMessageCount(targetUserId);
      if (currentMessageCount === null) {
        console.warn(
          "[MF] Vérification XP reportée : comptage indisponible."
        );
        return;
      }
      const currentActivityScore = getUserActivityScore(currentUserData, currentMessageCount);
      const currentLevelKey = getLevelTitle(currentActivityScore);
      const currentLevelNumber = getLevelNumber(currentActivityScore);

      const previousLevelNumberRaw = currentUserData?.xp?.lastNotifiedLevelNumber;
      const previousLevelNumber = Number(previousLevelNumberRaw);

      const userRef = doc(db, "users", targetUserId);

      const levelTrackingPayload = {
        "xp.lastNotifiedLevelKey": currentLevelKey,
        "xp.lastNotifiedLevelNumber": currentLevelNumber,
      };

      // Première initialisation : on enregistre le niveau actuel sans spammer l'utilisateur.
      if (!Number.isFinite(previousLevelNumber)) {
        await updateDoc(userRef, levelTrackingPayload);
        return;
      }

      // Notification seulement si l'utilisateur monte vraiment de niveau.
      if (currentLevelNumber > previousLevelNumber) {
        await notificationService.createLevelUpNotification(
          targetUserId,
          currentLevelKey,
          currentLevelNumber
        );

        await updateDoc(userRef, levelTrackingPayload);
      }
    } catch (error) {
      console.error("UserContext: erreur lors de la vérification level_up", error);
    }
  }, [fetchUserMessageCount]);

  const lastXpSourcesSignatureRef = useRef(null);

  useEffect(() => {
    if (!userId) {
      lastXpSourcesSignatureRef.current = null;
      return;
    }

    const userRef = doc(db, "users", userId);

    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data();

        // ======================================================
        // ABONNEMENT — synchronisation temps réel dans UserContext
        // ======================================================

        const liveSubscription =
          data?.subscription || defaultSubscription;

        setUserData((currentUserData) => {
          if (!currentUserData) {
            return currentUserData;
          }

          const currentSubscription =
            currentUserData.subscription || defaultSubscription;

          const subscriptionChanged =
            currentSubscription.planId !== liveSubscription.planId ||
            currentSubscription.status !== liveSubscription.status ||
            currentSubscription.billingPeriod !== liveSubscription.billingPeriod ||
            currentSubscription.currentPeriodEnd !== liveSubscription.currentPeriodEnd ||
            currentSubscription.paypalSubscriptionId !== liveSubscription.paypalSubscriptionId;

          if (!subscriptionChanged) {
            return currentUserData;
          }

          console.log(
            "UserContext: abonnement synchronisé en temps réel",
            {
              planId: liveSubscription.planId,
              status: liveSubscription.status,
            }
          );

          return {
            ...currentUserData,
            subscription: liveSubscription,
          };
        });

        // ======================================================
        // XP — logique temps réel déjà existante
        // ======================================================

        const xpSources = data?.xp?.sources || {};

        const xpSignature = JSON.stringify(xpSources);

        // On ne relance la vérification que si les sources XP
        // ont réellement changé.
        if (
          lastXpSourcesSignatureRef.current !== null &&
          lastXpSourcesSignatureRef.current === xpSignature
        ) {
          return;
        }

        lastXpSourcesSignatureRef.current = xpSignature;

        checkAndNotifyLevelUp(userId, data);
      },
      (error) => {
        console.error(
          "UserContext: erreur écoute temps réel XP",
          error
        );
      }
    );

    return () => unsubscribe();
  }, [userId, checkAndNotifyLevelUp]);

  // Mise à jour du nombre de messages de l'utilisateur
  const updateMessageCount = useCallback(async (userId) => {
    if (userId) {
      const count = await fetchUserMessageCount(userId);

      if (count !== null) {
        setMessageCount(count);
      }
    }
  }, [fetchUserMessageCount, setMessageCount]);


  // Appeler la fonction de comptage des messages
  useEffect(() => {
    if (!userId) return;

    updateMessageCount(userId);
  }, [userId, updateMessageCount]);


  return (
    <UserContext.Provider value={{
      username, setUsername, email, setEmail,
      avatar, setAvatar, banner, setBanner,
      isLoggedIn, setIsLoggedIn, updateContext, resetContext, fetchUserData,
      userIsLoggedIn, setUserIsLoggedIn,
      userId, setUserId, redirectTo, setRedirectTo, messageCount, updateMessageCount, setMessageCount, fetchUserMessageCount,
      userData, setUserData, subscription: userData?.subscription || defaultSubscription
    }}>
      {props.children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;






